import {useInfiniteQuery} from '@tanstack/react-query'
import {useSnackbar} from 'notistack'
import {useEffect, useMemo, useRef, useState} from 'react'
import {FacesFilter} from 'src/pages/facial-recognition'
import api, {FaceDetection} from 'src/services/api'
import {buildFilter} from 'src/utilities/buildFilter'
import {QueryKeyEnum} from 'src/utilities/enums/QueryKeyEnum'
import {FaceDetectionWSChannelEnum, WebSocketRoomTypeEnum} from 'src/utilities/enums/WSChannelEnums'
import {DetectedFacesFilter} from 'src/utilities/filterBuilder'
import handleErrorWithSnackbar from 'src/utilities/handleErrorWithSnackbar'
import useWebsocket from '../socket/useWebsocket'

const BATCH_SIZE = 50

type Props = {
  filters?: FacesFilter
  search?: string
  start?: string
  end?: string
  groupFaces?: boolean
}

export default function useFaceDetectionsMosaic({filters, search, start, end, groupFaces}: Props) {
  const filtersRef = useRef(filters)

  useEffect(() => {
    filtersRef.current = filters
  }, [filters?.companyIds])

  const onMessage = (event: FaceDetection) => {
    if (!filtersRef.current?.companyIds?.includes(event.companyId)) return

    setFaceDetections((prevFaceDetections) => {
      const newFaceDetections = [event, ...(prevFaceDetections ?? [])]
        .sort((a, b) => b.id - a.id)
        .filter((detection, index, self) => index === self.findIndex((t) => t.id === detection.id))
        .filter((detection) => filtersRef.current?.companyIds?.includes(detection.companyId))

      return newFaceDetections
    })
  }

  const websocket = useWebsocket({
    listenTo: [FaceDetectionWSChannelEnum.FACE_DETECTION],
    room: WebSocketRoomTypeEnum.FACE,
    onMessage,
  })

  const [pageCount, setPageCount] = useState(1)
  const {enqueueSnackbar} = useSnackbar()

  const [faceDetections, setFaceDetections] = useState<Array<FaceDetection>>([])

  const query = useInfiniteQuery({
    retry: 1,
    refetchOnWindowFocus: false,
    initialPageParam: 0,
    queryKey: [QueryKeyEnum.FACE_DETECTIONS, filters, search, start, end],
    queryFn: async ({pageParam: nextId}) => {
      if (nextId === -1) throw new Error('No more pages to fetch')
      setFaceDetections([])

      const defaultFilter = new DetectedFacesFilter({
        cameraId: filters?.cameraClientId,
        companyId: filters?.companyIds,
        listName: filters?.listName,
        cameraClientId: filters?.clientIds,
        faceName: filters?.faceName,
      })
      const filter = buildFilter(DetectedFacesFilter, defaultFilter, {search, searchIn: ['faceName', 'cameraName']})

      const response = await api.events.getFaceDetectionsWithAdvancedFiltering({
        batchSize: BATCH_SIZE,
        includes: ['face', 'faceDetectionsHaveActions', 'face.faceImages'],
        filters: filter,
        orderBy: [{field: 'createdAt', order: 'DESC'}],
        start: start,
        end: end,
        startId: nextId === 0 ? undefined : nextId,
      })

      if (nextId !== 0) setPageCount((prev) => prev + 1)

      return response
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage && allPages && allPages.some((c) => c && c.data.data.items.some((detection) => detection.id === lastPage.data.data.nextId))) {
        return null
      }

      return lastPage ? lastPage.data.data.nextId : undefined
    },
    throwOnError: () => {
      return false
    },
  })

  const getEmphasisFaceDetections = (faceDetections: Array<FaceDetection>) => {
    return faceDetections.filter((faceDetection) => faceDetection.face && !!faceDetection.face.name).slice(0, 4)
  }

  useEffect(() => {
    if (query.error && query.error.message === 'No more pages to fetch') return

    if (query.error) {
      handleErrorWithSnackbar(enqueueSnackbar, query.error, 'Falha ao carregar detecções faciais')
      setFaceDetections([])
    }
  }, [query.error])

  const getCombinedFaceDetections = (pageCount: number) => {
    return [...(faceDetections ?? []), ...(query.data?.pages.flatMap((page) => page.data.data.items) ?? [])]
      .filter((detection, index, self) => index === self.findIndex((t) => t.id === detection.id))
      .slice(0, BATCH_SIZE * pageCount)
  }

  const combinedDetections = useMemo(() => {
    const paginatedDetections = query.data?.pages.flatMap((page) => page.data.data.items) ?? []
    const merged = [...faceDetections, ...paginatedDetections]
      .filter((detection, index, self) => index === self.findIndex((t) => t.id === detection.id))
      .slice(0, BATCH_SIZE * pageCount)

    if (groupFaces) {
      const unique = Array.from(new Map(merged.map((item) => [item.face?.id, item])).values())
      websocket.forceDisconnect()
      return unique.slice(0, BATCH_SIZE * pageCount)
    }

    return merged
  }, [faceDetections, query.data, pageCount, groupFaces])

  return {
    ...query,
    data: combinedDetections,
    emphasisFaceDetections: getEmphasisFaceDetections(getCombinedFaceDetections(pageCount)),
    websocket,
  }
}
